/* eslint-disable react/require-default-props */
import React from 'react';
import { arrayOf, func, number, shape, string } from 'prop-types';
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

// =============================================================================
// FilterBar Component
// =============================================================================
function FilterBar({
  filters = {},
  onFilterChange,
  onReset,
  filterConfig = [],
  filterOptions = {},
}) {
  return (
    <Box>
      {/* Dynamic Filter Fields - Rendered in Config Order */}
      {filterConfig.map(config => {
        // If type is 'search', render search field
        if (config.type === 'search') {
          return (
            <FormControl key={config.field} sx={{ m: '5px' }}>
              <TextField
                label={config.label || 'Search'}
                onChange={e => onFilterChange(config.field, e.target.value)}
                placeholder={config.placeholder}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
                value={filters[config.field] || ''}
                variant="outlined"
              />
            </FormControl>
          );
        }
        // If custom component provided, use it
        if (config.component) {
          const CustomComponent = config.component;
          return (
            <Box key={config.field}>
              <CustomComponent
                config={config}
                filterOptions={filterOptions}
                onChange={value => onFilterChange(config.field, value)}
                value={filters[config.field] || ''}
              />
            </Box>
          );
        }

        // If type is 'select', render Select dropdown
        if (config.type === 'select') {
          return (
            <FormControl key={config.field} size="small" sx={{ m: '5px', minWidth: 200 }}>
              <InputLabel>{config.label}</InputLabel>
              <Select
                label={config.label}
                onChange={e => onFilterChange(config.field, e.target.value)}
                value={filters[config.field] || ''}
              >
                <MenuItem value="">{config.allLabel || 'All'}</MenuItem>
                {filterOptions[config.optionsKey]?.map(option => {
                  // Handle both simple arrays and object arrays
                  const value = config.valueKey ? option[config.valueKey] : option;
                  const label = config.labelKey ? option[config.labelKey] : option;
                  return (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          );
        }

        // Default: render TextField
        return (
          <FormControl key={config.field} sx={{ m: '5px' }}>
            <TextField
              label={config.label}
              onChange={e => onFilterChange(config.field, e.target.value)}
              placeholder={config.placeholder || `Filter by ${config.label}`}
              value={filters[config.field] || ''}
              variant="outlined"
            />
          </FormControl>
        );
      })}
      {/* Reset Button */}
      <Button onClick={onReset} sx={{ m: 1.5 }}>
        Clear
      </Button>
    </Box>
  );
}

FilterBar.propTypes = {
  filterConfig: arrayOf(
    shape({
      allLabel: string,
      component: func, // Custom component to render
      field: string.isRequired,
      label: string,
      labelKey: string,
      minWidth: number,
      operator: string,
      optionsKey: string, // Required only for select type
      placeholder: string,
      type: string, // 'text' (default), 'select', 'search', or custom
      valueKey: string,
    })
  ),
  filterOptions: shape({}),
  filters: shape({}),
  onFilterChange: func.isRequired,
  onReset: func.isRequired,
};

export default FilterBar;
